import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, IconButton, Grid, Typography } from '@mui/material';
import { Delete, Add } from '@mui/icons-material';

// Interface for props
interface ManualEntryModalProps {
    data: any[];
    dialogOpen: boolean;
    setDialogOpen: (open: boolean) => void;
}

interface ManualRow {
    id: number;
    description: string;
    quantity: number;
    price: number;
}

const ManualEntryModal: React.FC<ManualEntryModalProps> = ({ data = [], dialogOpen, setDialogOpen }) => {
    const [rows, setRows] = useState<ManualRow[]>([]);

    const totalPrice = rows.reduce((sum, row) => sum + row.quantity * row.price, 0);

    // Add a new row
    const handleAddRow = () => {
        setRows([...rows, { id: Date.now(), description: '', quantity: 1, price: 0 }]);
    };

    // Delete a row
    const handleDeleteRow = (id: number) => {
        setRows(rows.filter((row) => row.id !== id));
    };

    // Update row values
    const handleRowChange = (id: number, key: keyof ManualRow, value: string | number) => {
        setRows(rows.map((row) => (row.id === id ? { ...row, [key]: value } : row)));
    };

    const handleDialogClose = () => setDialogOpen(false);

    const handleConfirmSelection = () => {
        console.log('Selected rows:', rows);
        setDialogOpen(false);
    };

    return (
        <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth>
            <DialogTitle sx={{ textAlign: 'center' }}>שורות ידניות</DialogTitle>
            <DialogContent>
                {/* Total Price Row */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Grid item xs={12}>
                        <Typography variant="h6" align="right">
                            סך הכל: {totalPrice.toFixed(2)} ₪
                        </Typography>
                    </Grid>
                </Grid>

                {/* Column Headers */}
                <Grid container spacing={2} sx={{ fontWeight: 'bold', mb: 1 }}>
                    <Grid item xs={1}></Grid>
                    <Grid item xs={5}>
                        תיאור
                    </Grid>
                    <Grid item xs={3}>
                        כמות
                    </Grid>
                    <Grid item xs={3}>
                        מחיר
                    </Grid>
                </Grid>

                {/* Rows */}
                {rows.map((row) => (
                    <Grid container spacing={2} alignItems="center" key={row.id} sx={{ mb: 1 }}>
                        {/* Delete Button */}
                        <Grid item xs={1}>
                            <IconButton onClick={() => handleDeleteRow(row.id)} color="error">
                                <Delete />
                            </IconButton>
                        </Grid>

                        {/* Description */}
                        <Grid item xs={5}>
                            <TextField
                                fullWidth
                                value={row.description}
                                onChange={(e) => handleRowChange(row.id, 'description', e.target.value)}
                                placeholder="תיאור"
                            />
                        </Grid>

                        {/* Quantity */}
                        <Grid item xs={3}>
                            <TextField
                                fullWidth
                                type="number"
                                value={row.quantity}
                                onChange={(e) => handleRowChange(row.id, 'quantity', +e.target.value)}
                                inputProps={{ min: 1 }}
                            />
                        </Grid>

                        {/* Price */}
                        <Grid item xs={3}>
                            <TextField
                                fullWidth
                                type="number"
                                value={row.price}
                                onChange={(e) => handleRowChange(row.id, 'price', +e.target.value)}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                    </Grid>
                ))}

                {/* Add Row Button */}
                <Button onClick={handleAddRow} startIcon={<Add />} variant="outlined" fullWidth>
                    הוסף שורה
                </Button>
            </DialogContent>

            {/* Actions */}
            <DialogActions sx={{ justifyContent: 'center' }}>
                <Button onClick={handleConfirmSelection} color="primary" variant="contained">
                    בחר
                </Button>
                <Button onClick={handleDialogClose} color="secondary" variant="contained">
                    בטל
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ManualEntryModal;
